import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DoorOpen, Loader2 } from 'lucide-react';
import { visitorThreadLinkAPI } from '../lib/api';
import VisitorConversation from '../components/property/VisitorConversation';

const LANGS = [{ id: 'en', label: 'EN' }, { id: 'fr', label: 'FR' }, { id: 'es', label: 'ES' }];

// The link inside the resident's text message: it opens ONE visitor
// conversation, with no login, and expires with that 2-hour visitor session.
// Nothing else in the resident portal is reachable from here.
export default function VisitorThreadLink() {
    const { linkToken } = useParams();
    const { t, i18n } = useTranslation();
    const [thread, setThread] = useState(null);
    const [expired, setExpired] = useState(false);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const deadRef = useRef(false);

    const labels = {
        empty: t('visitorThread.threadEmpty'), placeholder: t('visitorThread.placeholder'),
        notifyVisitor: t('visitorThread.notifyVisitor'), notifiedSent: t('visitorThread.notified'),
        closed: t('visitorThread.closed'), privacyNote: t('visitorThread.privacy'),
        you: t('visitorThread.you'), visitor: t('visitorThread.visitor'),
        autoReply: t('visitorThread.autoReply'), chose: t('visitorThread.chose'),
        atDoorNow: t('visitorThread.atDoorNow'), notAtDoor: t('visitorThread.notAtDoor'),
    };

    const load = useCallback(async () => {
        try {
            const { data } = await visitorThreadLinkAPI.thread(linkToken);
            setThread(data);
            deadRef.current = !!data.thread_closed;
        } catch (e) {
            if ([404, 410].includes(e?.response?.status)) { deadRef.current = true; setExpired(true); }
        }
    }, [linkToken]);

    useEffect(() => {
        try {
            const stored = localStorage.getItem('yulontap_lang');
            if (stored && LANGS.some((l) => l.id === stored)) i18n.changeLanguage(stored);
        } catch (_e) { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(() => { if (!deadRef.current) load(); }, 5000);
        return () => clearInterval(id);
    }, [load]);

    const send = async () => {
        const text = input.trim();
        if (!text) return;
        setBusy(true); setError(''); setInput('');
        try {
            const { data } = await visitorThreadLinkAPI.reply(linkToken, text);
            setThread((tr) => ({ ...(tr || {}), messages: [...((tr || {}).messages || []), data.message] }));
        } catch (e) {
            setInput(text);
            if (e?.response?.status === 410) { deadRef.current = true; setExpired(true); }
            else setError(t('visitorThread.error'));
        } finally { setBusy(false); }
    };

    const notify = async () => {
        setBusy(true); setError('');
        try {
            const { data } = await visitorThreadLinkAPI.notify(linkToken);
            setThread((tr) => ({ ...(tr || {}), notified_at: data.notified_at }));
        } catch (e) {
            if (e?.response?.status === 410) { deadRef.current = true; setExpired(true); }
            else setError(t('visitorThread.error'));
        } finally { setBusy(false); }
    };

    const setLang = (id) => {
        i18n.changeLanguage(id);
        try { localStorage.setItem('yulontap_lang', id); } catch (_e) { /* ignore */ }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="visitor-thread-page"
            style={{ fontFamily: 'Manrope, sans-serif' }}>
            <header className="flex items-center justify-between border-b border-white/5 px-5 py-4">
                <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-400/30 bg-cyan-400/10">
                        <DoorOpen className="h-5 w-5 text-cyan-300" />
                    </div>
                    <span className="text-sm font-bold uppercase tracking-wide text-slate-200"
                        style={{ fontFamily: 'Outfit, sans-serif' }}>{t('visitorThread.title')}</span>
                </div>
                <div className="flex items-center gap-1" data-testid="visitor-thread-lang">
                    {LANGS.map((l) => (
                        <button key={l.id} type="button" onClick={() => setLang(l.id)}
                            data-testid={`visitor-thread-lang-${l.id}`}
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold tracking-wide transition-colors ${i18n.language === l.id ? 'bg-cyan-400 text-slate-950' : 'text-slate-400 hover:text-slate-200'}`}>
                            {l.label}
                        </button>
                    ))}
                </div>
            </header>

            <main className="mx-auto w-full max-w-md px-5 py-6">
                {expired ? (
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-12 text-center"
                        data-testid="visitor-thread-expired">
                        <p className="text-base font-bold text-white">{t('visitorThread.expiredTitle')}</p>
                        <p className="mt-2 text-sm text-slate-400">{t('visitorThread.expiredHint')}</p>
                    </div>
                ) : thread === null ? (
                    <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-cyan-300" /></div>
                ) : (
                    <>
                        <h1 className="text-xl font-extrabold tracking-tight text-white"
                            style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="visitor-thread-heading">
                            {t('visitorThread.heading', { entrance: thread.entrance || '' })}
                        </h1>
                        <p className="mb-5 mt-1 text-sm text-slate-400" data-testid="visitor-thread-unit">
                            {t('visitorThread.unit')} {thread.unit_number}
                        </p>
                        {error && <p className="mb-2 text-sm text-rose-400" data-testid="visitor-thread-error">{error}</p>}
                        <VisitorConversation prefix="vtl" labels={labels}
                            messages={thread.messages || []} input={input} onInputChange={setInput}
                            onSend={send} onNotify={thread.can_reply ? notify : null}
                            live={!!thread.live} closed={!!thread.thread_closed}
                            notifiedAt={thread.notified_at} busy={busy} />
                    </>
                )}
            </main>
        </div>
    );
}
